import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Calendar,
  Video,
  Clock,
  Ticket,
  Settings,
  LogOut,
  BookOpen,
  BookMarked,
  Menu,
  X,
  Image,
  Heart,
  HelpCircle,
  FileText,
  Images,
  MessageSquare,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  User,
  Home,
  ChevronDown,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('admin_sidebar_collapsed') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const auth = localStorage.getItem('admin_auth');
    if (!auth) {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    try {
      localStorage.setItem('admin_sidebar_collapsed', String(collapsed));
    } catch (e) {
      /* noop */
    }
  }, [collapsed]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_auth');
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const navSections = [
    {
      title: 'Vue d\'ensemble',
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
      ],
    },
    {
      title: 'Contenu',
      items: [
        { icon: Image, label: 'Page d\'accueil', path: '/admin/hero' },
        { icon: Calendar, label: 'Événements', path: '/admin/events' },
        { icon: Ticket, label: 'Tickets', path: '/admin/tickets' },
        { icon: Video, label: 'Vidéos', path: '/admin/videos' },
        { icon: Clock, label: 'Timeline', path: '/admin/timeline' },
        { icon: BookMarked, label: 'Histoire', path: '/admin/histoire' },
        { icon: Sparkles, label: 'Expérience', path: '/admin/experience' },
        { icon: Heart, label: 'Valeurs', path: '/admin/values' },
        { icon: HelpCircle, label: 'FAQ', path: '/admin/faqs' },
        { icon: FileText, label: 'Contenu', path: '/admin/content' },
        { icon: Images, label: 'Galerie', path: '/admin/gallery' },
        { icon: MessageSquare, label: 'Ruban défilant', path: '/admin/ticker' },
      ],
    },
    {
      title: 'Gestion',
      items: [
        { icon: BookOpen, label: 'Réservations VIP', path: '/admin/bookings' },
        { icon: Settings, label: 'Paramètres', path: '/admin/settings' },
      ],
    },
  ];

  const allNavItems = navSections.flatMap((s) => s.items);

  const isActive = (path) => location.pathname === path;

  const getBreadcrumbs = () => {
    const active = allNavItems.find((i) => i.path === location.pathname);
    if (active) {
      return [
        { label: 'Admin', path: '/admin/dashboard' },
        { label: active.label, path: active.path, current: true },
      ];
    }
    return [{ label: 'Admin', path: '/admin/dashboard', current: true }];
  };

  const breadcrumbs = getBreadcrumbs();

  const NavItem = ({ item, showLabel }) => {
    const active = isActive(item.path);
    return (
      <Link
        to={item.path}
        onClick={() => setMobileOpen(false)}
        className="relative block group"
        title={!showLabel ? item.label : undefined}
      >
        <motion.div
          whileHover={{ x: showLabel ? 2 : 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all overflow-hidden ${
            active
              ? 'text-white shadow-lg'
              : 'text-[#B9B2A8] hover:bg-white/5 hover:text-[#F5F0E8]'
          }`}
          style={
            active
              ? {
                  background:
                    'linear-gradient(135deg, rgba(201,168,76,0.9) 0%, rgba(163,135,62,0.85) 100%)',
                  boxShadow:
                    '0 4px 16px -4px rgba(201,168,76,0.55), inset 0 1px 0 rgba(255,255,255,0.15)',
                }
              : undefined
          }
        >
          {active && (
            <motion.span
              layoutId="sidebar-active-glow"
              className="absolute inset-0 rounded-lg pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle at 30% 50%, rgba(255,220,150,0.35) 0%, transparent 60%)',
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />
          )}
          <motion.div
            whileHover={{ scale: 1.15, rotate: active ? 0 : 5 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            className="relative z-10 flex-shrink-0"
          >
            <item.icon className="w-[18px] h-[18px]" />
          </motion.div>
          {showLabel && (
            <span className="relative z-10 font-medium truncate">{item.label}</span>
          )}
          {active && showLabel && (
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              className="ml-auto w-1.5 h-1.5 rounded-full bg-white/80 relative z-10"
            />
          )}
        </motion.div>
      </Link>
    );
  };

  const NavContent = ({ showLabels = true, onCollapseToggle = null }) => (
    <>
      <div className="p-4 border-b border-white/10 flex items-center justify-between gap-2">
        <Link to="/" className="block min-w-0 flex-1">
          {showLabels ? (
            <>
              <h2 className="font-display text-lg text-[#C9A84C] truncate tracking-wide">
                INVASION LATINA
              </h2>
              <p className="text-[11px] text-[#B9B2A8] uppercase tracking-wider">
                Admin Panel
              </p>
            </>
          ) : (
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C9A84C] to-[#a3873e] flex items-center justify-center font-display text-[#060606] font-bold text-sm">
              IL
            </div>
          )}
        </Link>
        {onCollapseToggle && (
          <button
            onClick={onCollapseToggle}
            className="hidden lg:flex w-7 h-7 items-center justify-center rounded-md bg-white/5 hover:bg-white/10 text-[#B9B2A8] hover:text-[#F5F0E8] transition-colors flex-shrink-0"
            aria-label={collapsed ? 'Déplier' : 'Replier'}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        )}
      </div>

      <nav className="flex-1 p-3 space-y-5 overflow-y-auto custom-scrollbar">
        {navSections.map((section) => (
          <div key={section.title}>
            {showLabels && (
              <div className="px-3 mb-2 text-[10px] font-semibold text-[#6B645A] uppercase tracking-[0.15em]">
                {section.title}
              </div>
            )}
            <div className="space-y-1">
              {section.items.map((item) => (
                <NavItem key={item.path} item={item} showLabel={showLabels} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-white/10">
        <Link
          to="/"
          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#B9B2A8] hover:bg-white/5 hover:text-[#F5F0E8] transition-colors mb-1 ${
            !showLabels ? 'justify-center' : ''
          }`}
          title={!showLabels ? 'Voir le site' : undefined}
        >
          <Home className="w-[18px] h-[18px] flex-shrink-0" />
          {showLabels && <span>Voir le site</span>}
        </Link>
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#B9B2A8] hover:bg-red-500/10 hover:text-red-400 transition-colors ${
            !showLabels ? 'justify-center' : ''
          }`}
          title={!showLabels ? 'Déconnexion' : undefined}
        >
          <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
          {showLabels && <span>Déconnexion</span>}
        </button>
      </div>
    </>
  );

  const sidebarWidth = collapsed ? 'w-20' : 'w-64';
  const contentMargin = collapsed ? 'lg:ml-20' : 'lg:ml-64';

  return (
    <div className="min-h-screen bg-[#060606] flex" data-testid="admin-layout">
      {/* Ambient background decor */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-[#C9A84C]/5 blur-3xl" />
        <div className="absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full bg-purple-500/5 blur-3xl" />
      </div>

      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 80 : 256 }}
        transition={{ type: 'spring', stiffness: 260, damping: 30 }}
        className="hidden lg:flex flex-col bg-[#0B0B0C]/95 backdrop-blur-xl border-r border-white/10 fixed h-screen z-30"
      >
        <NavContent showLabels={!collapsed} onCollapseToggle={() => setCollapsed(!collapsed)} />
      </motion.aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-50 bg-[#0B0B0C]/95 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C9A84C] to-[#a3873e] flex items-center justify-center font-display text-[#060606] font-bold text-sm">
            IL
          </div>
          <h2 className="font-display text-base text-[#C9A84C] tracking-wide">INVASION LATINA</h2>
        </Link>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-[#F5F0E8]">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-72 bg-[#0B0B0C]/95 backdrop-blur-xl border-r border-white/10 p-0 flex flex-col"
          >
            <NavContent showLabels={true} />
          </SheetContent>
        </Sheet>
      </div>

      {/* Main Content */}
      <main className={`flex-1 ${contentMargin} pt-16 lg:pt-0 relative z-10 transition-all duration-300`}>
        {/* Top bar - desktop only */}
        <div className="hidden lg:flex sticky top-0 z-20 bg-[#060606]/80 backdrop-blur-xl border-b border-white/5 px-8 h-16 items-center justify-between">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={crumb.path}>
                {idx > 0 && (
                  <ChevronRight className="w-3.5 h-3.5 text-[#6B645A]" />
                )}
                {crumb.current ? (
                  <span className="text-[#F5F0E8] font-medium">{crumb.label}</span>
                ) : (
                  <Link
                    to={crumb.path}
                    className="text-[#B9B2A8] hover:text-[#C9A84C] transition-colors"
                  >
                    {crumb.label}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A84C] to-[#a3873e] flex items-center justify-center shadow-[0_2px_8px_rgba(201,168,76,0.4)]">
                <User className="w-4 h-4 text-[#060606]" />
              </div>
              <span className="text-sm text-[#F5F0E8] font-medium">admin</span>
              <ChevronDown
                className={`w-3.5 h-3.5 text-[#B9B2A8] transition-transform ${
                  userMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            <AnimatePresence>
              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.96 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-12 w-56 bg-[#0B0B0C]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-white/10">
                    <div className="text-sm text-[#F5F0E8] font-medium">Administrateur</div>
                    <div className="text-xs text-[#B9B2A8]">admin@invasionlatina.be</div>
                  </div>
                  <div className="py-1">
                    <Link
                      to="/admin/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-[#B9B2A8] hover:bg-white/5 hover:text-[#F5F0E8] transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      Paramètres
                    </Link>
                    <Link
                      to="/"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-[#B9B2A8] hover:bg-white/5 hover:text-[#F5F0E8] transition-colors"
                    >
                      <Home className="w-4 h-4" />
                      Voir le site
                    </Link>
                  </div>
                  <div className="border-t border-white/10 py-1">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Déconnexion
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Page content with animated transitions */}
        <div className="p-4 sm:p-6 lg:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Custom scrollbar styling */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.15); }
      `}</style>
    </div>
  );
};

export default AdminLayout;
